/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {closeModal, closeSecondaryModal, setHeadbarTitle} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import clsx from 'clsx'
import EditLeadDetailTab from './EditLeadModalComponents/EditLeadDetailTab'
import EditLeadOffersTab from './EditLeadModalComponents/EditLeadOffersTab'
import EmailTemplatesTab from 'components/EmailTemplates/EmailTemplatesTab'
import UiTabSwitcher from 'components/Ui/UiTabSwitcher'
import CreateOfferModal from 'components/Offers/CreateOfferModal'
import EditCompanyModal from 'components/Companies/EditCompanyModal'
import CompanyContactForm from 'components/Companies/forms/CompanyContactForm'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import {LeadStatus} from 'types/enums/LeadStatus'
import type {Lead} from 'types/entities/Lead'
import {getOffersCalculationRevenueRange} from 'utils/offers/offersUtils'
import {useHeadbarReminders} from 'utils/hooks/useHeadbarReminders'
import {useLeadContext} from 'utils/hooks/useLeadContext'
import {useEditLeadTaskNoteHandlers} from 'utils/hooks/useEditLeadTaskNoteHandlers'
import {useEditLeadTabNavigation} from 'utils/hooks/useEditLeadTabNavigation'
import {useEditLeadStatusChange} from 'utils/hooks/useEditLeadStatusChange'
import UiLeadStatusButtonGroup from './UiLeadStatusButtonGroup'
import Dates from 'utils/Dates'
import {Tooltip} from 'react-tooltip'
import {UiTabVariant} from 'types/enums/UiTabVariant'
import {EditLeadModalTab} from 'types/enums/specific/EditLeadModalTab'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import type {Company} from 'types/entities/Company'
import {
    buildOpportunityName,
    parseAnlasstypFromOpportunityName,
} from 'utils/leads/opportunityNameUtils'
import {buildLeadCreatePayload} from 'utils/leads/useInlineLeadPillUpdate'

interface EditLeadModalProps {
    lead: Lead
    onSuccess?: () => void
    isSecondary?: boolean
    initialTab?: EditLeadModalTab
    embedded?: boolean
}

const EditLeadModal: React.FC<EditLeadModalProps> = (props: EditLeadModalProps) => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const secondaryOpen = useTypedSelector(state => !!state.ui?.secondaryModal?.isOpen)
    const headbarTitle = useTypedSelector(state => state.ui?.headbarTitle)
    const [activeTab, setActiveTab] = React.useState<EditLeadModalTab>(props.initialTab ?? EditLeadModalTab.Detail)
    const {state: leadState, actions: action, isOpportunity} = useLeadContext(props.lead.category)
    const selectedLeadFromState =
        leadState.selectedLead?.id === props.lead?.id ? leadState.selectedLead : null
    const selectedLead = selectedLeadFromState || props.lead

    const hasCompany = Boolean(selectedLead?.company_id || selectedLead?.company_name?.trim())
    const hasContactPerson = Boolean(
        selectedLead?.contact_id ||
        selectedLead?.contact_person?.trim() ||
        selectedLead?.full_name?.trim()
    )
    const isCreateOfferEnabled = hasCompany && hasContactPerson

    useHeadbarReminders(true, ReminderModelName.Lead, props.lead.id)

    const handleSuccess = (_lead?: Lead, close: boolean = true) => {
        if (props.onSuccess) props.onSuccess()
        if (close) {
            if (props.embedded) return
            if (props.isSecondary) {
                dispatch(closeSecondaryModal())
            } else {
                dispatch(closeModal())
            }
            dispatch(setHeadbarTitle(null))
        }
    }

    const handleReloadLead = useCallback(async () => {
        if (selectedLead) {
            await dispatch(action.fetchLeadById(selectedLead))
        }
    }, [dispatch, action, selectedLead])

    const handleAccountChange = useCallback(
        async (company: Company) => {
            if (!selectedLead) return

            const previousAccountName = selectedLead.company_name?.trim() || ''
            const anlasstyp = parseAnlasstypFromOpportunityName(
                selectedLead.name || '',
                previousAccountName
            )
            const previousGeneratedName = buildOpportunityName(anlasstyp, previousAccountName)
            const nextGeneratedName = buildOpportunityName(anlasstyp, company.name)
            const isAutoGeneratedName =
                isOpportunity &&
                Boolean(anlasstyp) &&
                selectedLead.name?.trim() === previousGeneratedName

            if (isAutoGeneratedName && nextGeneratedName && nextGeneratedName !== selectedLead.name) {
                const updatedLead = await dispatch(
                    action.fetchCreateOrUpdateLead({
                        ...buildLeadCreatePayload(selectedLead, {}),
                        name: nextGeneratedName,
                        company_name: company.name,
                    })
                )

                if (updatedLead) {
                    dispatch(action.setSelectedLead(updatedLead))
                }
                return
            }

            await dispatch(action.fetchLeadById(selectedLead))
        },
        [action, dispatch, isOpportunity, selectedLead]
    )

    const {
        handleAddTask, handleEditTask, handleCompleteTask, handleDeleteTask,
        handleAddNote, handleEditNote, handleDeleteNote,
    } = useEditLeadTaskNoteHandlers({selectedLead, action, onReload: handleReloadLead})

    const createOfferInProgressIdRef = React.useRef<number | null>(null)

    const {handleTabClick} = useEditLeadTabNavigation({
        activeTab,
        setActiveTab,
        isCreateOfferEnabled,
        createOfferInProgressIdRef,
        selectedLead,
        onReloadLead: handleReloadLead,
    })

    const {handleStatusChange} = useEditLeadStatusChange({
        selectedLead,
        action,
        isOpportunity,
        isSecondary: props.isSecondary,
        onSuccess: props.onSuccess,
        renderOpportunityModal: (lead) => (
            <EditLeadModal lead={lead} onSuccess={props.onSuccess} isSecondary={props.isSecondary} />
        ),
    })

    const tasks = useMemo(() => selectedLead?.tasks?.slice().sort((a, b) => a.id - b.id) ?? [], [selectedLead?.tasks])
    const notes = selectedLead?.notes ?? []
    const effectiveOffers = useMemo(() => selectedLead?.offers ?? [], [selectedLead?.offers])
    const offersCount = effectiveOffers.length

    const {minRevenue: offersMinRevenue, maxRevenue: offersMaxRevenue} = useMemo(
        () => getOffersCalculationRevenueRange(effectiveOffers),
        [effectiveOffers]
    )

    const missingRequirementLabels: string[] = []
    if (!hasCompany) missingRequirementLabels.push(t('Customer') as string)
    if (!hasContactPerson) missingRequirementLabels.push(t('Contact Person') as string)

    const createOfferTooltip = missingRequirementLabels.length
        ? `${t('Missing')}: ${missingRequirementLabels.join(', ')}`
        : ''

    const tabs = [
        {id: EditLeadModalTab.Detail, name: isOpportunity ? t('Opportunity') : t('Lead')},
        ...(isOpportunity
            ? [{id: EditLeadModalTab.Offers, name: offersCount !== undefined ? `${t('Offers')} (${offersCount})` : t('Offers')}]
            : []),
        {id: EditLeadModalTab.Customer, name: t('Account')},
        {id: EditLeadModalTab.Contact, name: t('Contact')},
        {id: EditLeadModalTab.MailTemplate, name: t('Mail-Vorlage')},
    ]

    const visibleActiveTab: EditLeadModalTab = useMemo(() => {
        if (
            activeTab === EditLeadModalTab.Customer ||
            activeTab === EditLeadModalTab.Contact ||
            activeTab === EditLeadModalTab.MailTemplate
        ) {
            return activeTab
        }
        if (!isOpportunity && (activeTab === EditLeadModalTab.Offers || activeTab === EditLeadModalTab.CreateOffer)) {
            return EditLeadModalTab.Detail
        }
        if (isOpportunity && activeTab === EditLeadModalTab.CreateOffer && !isCreateOfferEnabled) {
            return EditLeadModalTab.Detail
        }
        return activeTab
    }, [activeTab, isOpportunity, isCreateOfferEnabled])

    useEffect(() => {
        if (props.embedded || (secondaryOpen && !props.isSecondary)) return
        const title = isOpportunity ? t('Edit Opportunity') : t('Edit Lead')
        dispatch(setHeadbarTitle(title))
    }, [dispatch, t, isOpportunity, props.embedded, props.isSecondary, secondaryOpen])

    useEffect(() => () => {
        if (!props.embedded) dispatch(setHeadbarTitle(null))
    }, [dispatch, props.embedded])

    useEffect(() => {
        // A nested offer clears its title on unmount; restore the still-open detail context.
        if (props.embedded || props.isSecondary || secondaryOpen || headbarTitle ||
            visibleActiveTab !== EditLeadModalTab.Detail) return
        dispatch(setHeadbarTitle(isOpportunity ? t('Edit Opportunity') : t('Edit Lead')))
    }, [dispatch, t, isOpportunity, props.embedded, props.isSecondary, secondaryOpen, headbarTitle, visibleActiveTab])

    const loadedLeadRef = useRef<{id: number; action: typeof action} | null>(null)
    useEffect(() => {
        if (props.lead?.id) {
            if (loadedLeadRef.current?.id === props.lead.id && loadedLeadRef.current.action === action) return
            loadedLeadRef.current = {id: props.lead.id, action}
            dispatch(action.resetSelectedLead())
            dispatch(action.fetchLeadById(props.lead))
        }
    }, [dispatch, props.lead, action])

    return (
        <>
            <UiFormModalShell
                className={clsx('EditLeadModal')}
                title={t('Edit Lead')}
                subtitle={selectedLead?.name}
                showHeader={false}
                showHeaderCompact={!props.embedded}
                headerBottom={
                    <div className="h-16 flex items-center px-2 border-b border-gray-100 dark:border-gray-800">
                        <UiTabSwitcher
                            items={tabs}
                            variant={UiTabVariant.Underline}
                            selected={activeTab}
                            onClick={(tabId) => handleTabClick(tabId as EditLeadModalTab)}
                            className="h-full"
                        />
                        {isOpportunity && (
                            <button
                                type="button"
                                disabled={!isCreateOfferEnabled}
                                data-tooltip-id="create-offer-btn-tooltip"
                                data-tooltip-content={createOfferTooltip || undefined}
                                onClick={() => handleTabClick(EditLeadModalTab.CreateOffer)}
                                className={clsx(
                                    'ml-6 h-8 px-3 rounded text-sm font-semibold whitespace-nowrap transition-colors duration-200',
                                    isCreateOfferEnabled
                                        ? 'bg-cta-light text-white hover:opacity-90 cursor-pointer'
                                        : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed'
                                )}
                            >
                                {t('Create Offer')}
                            </button>
                        )}
                    </div>
                }
                headerCenter={
                    <UiLeadStatusButtonGroup
                        value={selectedLead?.status ?? (isOpportunity ? LeadStatus.NewInquiry : LeadStatus.New)}
                        onChange={handleStatusChange}
                        isOpportunity={isOpportunity}
                    />
                }
                headerActions={undefined}
                bodyClassName={clsx(
                    'EditLeadModalBody text-sm',
                    visibleActiveTab === EditLeadModalTab.Detail
                        ? 'grid grid-cols-12 gap-4 pb-6 px-4 items-stretch'
                        : 'flex flex-col h-full !overflow-hidden pb-6 px-6'
                )}
            >
                {visibleActiveTab === EditLeadModalTab.Detail && (
                    <EditLeadDetailTab
                        lead={props.lead}
                        selectedLead={selectedLead}
                        isOpportunity={isOpportunity}
                        hasCompany={hasCompany}
                        hasContactPerson={hasContactPerson}
                        notes={notes}
                        tasks={tasks}
                        onSuccess={handleSuccess}
                        onAddNote={handleAddNote}
                        onEditNote={handleEditNote}
                        onDeleteNote={handleDeleteNote}
                        onAddTask={handleAddTask}
                        onEditTask={handleEditTask}
                        onDeleteTask={handleDeleteTask}
                        onCompleteTask={handleCompleteTask}
                        onEditRejectionDetails={() => {
                            if (!selectedLead) {
                                return
                            }
                            handleStatusChange(selectedLead.status)
                        }}
                    />
                )}

                {visibleActiveTab === EditLeadModalTab.Customer && selectedLead && (
                    <div className="flex-1 flex flex-col pt-4 overflow-hidden">
                        {selectedLead.company_id ? (
                            <div className="flex-1 overflow-auto">
                                <EditCompanyModal
                                    company={selectedLead.company!}
                                    embedded={true}
                                    onSuccess={handleAccountChange}
                                />
                            </div>
                        ) : (
                            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800">
                                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                    {t('No customer assigned. Please assign a customer first.')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {visibleActiveTab === EditLeadModalTab.Contact && selectedLead && (
                    <div className="flex-1 flex flex-col pt-4 overflow-hidden">
                        {selectedLead.contact ? (
                            <CompanyContactForm
                                contact={selectedLead.contact}
                                fallbackSellerId={selectedLead.seller_id}
                                inline={true}
                                showSaveAndClose={false}
                                useSecondaryClose={false}
                                onChange={() => dispatch(action.fetchLeadById(selectedLead))}
                            />
                        ) : (
                            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800">
                                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                    {t('No contact assigned. Please assign a contact first.')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {visibleActiveTab === EditLeadModalTab.CreateOffer && isOpportunity && (
                    <div className="flex-1 flex flex-col pt-4 overflow-hidden">
                        <CreateOfferModal
                            leadId={selectedLead?.id}
                            companyId={selectedLead?.company_id}
                            preferredTime={selectedLead?.requested_time ?? undefined}
                            defaultValues={{
                                adults: Number(selectedLead?.persons) || 0,
                                date: selectedLead?.requested_date ? Dates.ymd(selectedLead.requested_date) : undefined,
                                contact_id: selectedLead?.contact_id ?? selectedLead?.contact?.id ?? undefined,
                            }}
                            embedded={true}
                            disableCompanyChange={!!selectedLead?.company_id}
                            onGuardStateChange={(id) => { createOfferInProgressIdRef.current = id }}
                            onCreated={() => {
                                createOfferInProgressIdRef.current = null
                                void handleReloadLead().catch(() => {})
                                setActiveTab(EditLeadModalTab.Detail)
                            }}
                        />
                    </div>
                )}

                {visibleActiveTab === EditLeadModalTab.Offers && isOpportunity && selectedLead && (
                    <EditLeadOffersTab
                        selectedLead={selectedLead}
                        offersCount={offersCount}
                        offersMinRevenue={offersMinRevenue}
                        offersMaxRevenue={offersMaxRevenue}
                        effectiveOffers={effectiveOffers}
                    />
                )}

                {visibleActiveTab === EditLeadModalTab.MailTemplate && selectedLead && (
                    <div className="flex-1 flex flex-col pt-4 overflow-hidden">
                        <EmailTemplatesTab
                            modelName="Lead"
                            modelId={selectedLead.id}
                            context={{lead: selectedLead, company: selectedLead.company}}
                        />
                    </div>
                )}
            </UiFormModalShell>
            {isOpportunity && !isCreateOfferEnabled && <Tooltip id="create-offer-requirements-tooltip" />}
        </>
    )
}

export default EditLeadModal
